import { useEffect, useState } from "react";
import { useForm, FieldErrors } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useFormExitGuard } from "@/hooks/use-form-exit-guard";
import { useSavingProgress } from "@/components/campos-formulario/form-shell/useSavingProgress";
import { comunicadoSistemaService } from "@/services/api/comunicado-sistema.service";
import {
  comunicadoSistemaKeys,
  comunicadoSistemaQueries,
} from "@/services/api/queries/comunicado-sistema.queries";
import {
  ComunicadoSistemaFormData,
  comunicadoSistemaSchema,
} from "@/validations/comunicado-sistema-validation-schema";
import { toComunicadoPayload } from "./comunicado-payload";

const COMUNICADOS_LIST = "/admin/comunicados";

/** Edição de um comunicado. O registro já é válido → o wizard nasce liberado. */
export function useComunicadoSistemaEditForm(id: number) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const saving = useSavingProgress();

  const { data, isLoading } = useQuery(comunicadoSistemaQueries.detail(id));

  const methods = useForm<ComunicadoSistemaFormData>({
    resolver: yupResolver(comunicadoSistemaSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      titulo: "",
      resumo: "",
      conteudo: "",
      nivel: "INFORMATIVO",
      inicioEm: new Date(),
      fimEm: null,
      cargos: [],
      areas: [],
    },
  });

  // Semeia o formulário quando o comunicado carrega.
  useEffect(() => {
    if (!data) return;
    methods.reset({
      titulo: data.titulo,
      resumo: data.resumo,
      conteudo: data.conteudo ?? "",
      nivel: data.nivel,
      inicioEm: new Date(data.inicioEm),
      fimEm: data.fimEm ? new Date(data.fimEm) : null,
      cargos: data.alvos.filter((a) => a.tipo === "CARGO").map((a) => a.valor),
      areas: data.alvos.filter((a) => a.tipo === "AREA").map((a) => a.valor),
    });
  }, [data, methods]);

  const { isSubmitting, isValid, isDirty } = methods.formState;
  const isSubmitDisabled = !isValid || isSubmitting;

  const [publicou, setPublicou] = useState(false);

  const salvar = (formData: ComunicadoSistemaFormData, publicar: boolean) => {
    if (isSubmitting) return;
    setPublicou(publicar);
    void saving.run(
      async () => {
        await comunicadoSistemaService.update(id, toComunicadoPayload(formData));
        if (publicar) await comunicadoSistemaService.publicar(id);
        await qc.invalidateQueries({ queryKey: comunicadoSistemaKeys.all });
        return true;
      },
      () => navigate(COMUNICADOS_LIST),
    );
  };

  const onSubmit = (formData: ComunicadoSistemaFormData) => salvar(formData, false);

  const onError = (errors: FieldErrors<ComunicadoSistemaFormData>) => {
    console.error("Validation errors:", errors);
  };

  const exitGuard = useFormExitGuard({
    isDirty,
    isSubmitting,
    onExit: () => navigate(COMUNICADOS_LIST),
  });

  return {
    isLoading,
    methods,
    onSubmit,
    onError,
    // Só no rascunho: republicar um vigente é no-op e "publicar" um arquivado o
    // traria de volta ao ar por um botão de salvar — desarquivar é ação da lista.
    onPublicar:
      data?.status === "RASCUNHO"
        ? methods.handleSubmit((formData) => salvar(formData, true), onError)
        : undefined,
    handleCancel: exitGuard.requestExit,
    exitGuard,
    isSubmitting,
    isSubmitDisabled,
    isEdit: true as const,
    submitLabel: "Salvar",
    publicou,
    savingOpen: saving.isOpen,
    savingStatus: saving.dialogStatus,
    handleSavingComplete: saving.handleComplete,
    handleSavingErrorClose: saving.reset,
  };
}

export type UseComunicadoSistemaEditFormReturn = ReturnType<typeof useComunicadoSistemaEditForm>;
